import type { DocumentPageSchema } from '@avisoma/shared';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { LazyImage } from '../../components/LazyImage';
import { FullScreenImagePreviewTrigger } from '../../components/FullScreenImagePreview';
import { pagePreviewSrc } from '../../lib/pagePreviewSrc';
import { SectionBandOverlay, sectionBandPct } from './SectionBandOverlay';
import { FieldBoxOverlay } from './FieldBoxOverlay';
import type { FieldHighlight } from './usePageTextLocations';
import { Switch } from '../../catalyst/switch';

const MIME_LABELS: Record<string, string> = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
};

function mimeTypeLabel(mimeType: string): string {
  return MIME_LABELS[mimeType] ?? 'Document';
}

interface PagePreviewCardProps {
  page: DocumentPageSchema;
  pdfUrl: string;
  // When set (a hovered child segment on the parent's Segments card), draw that
  // section's extracted band over this preview. The bounds carry the parent page
  // dims, so the band maps directly onto this (parent) page's preview.
  highlightBounds?: DocumentPageSchema['sectionBounds'];
  // DR-2004: which object to render — the stored (rotated) page or its
  // pre-rotation original. Lifted to the parent so the Actions download button
  // can follow the same toggle.
  variant: 'rotated' | 'original';
  onVariantChange: (variant: 'rotated' | 'original') => void;
  fieldHighlight?: FieldHighlight | null;
}

export function PagePreviewCard({
  page,
  pdfUrl,
  highlightBounds,
  variant,
  onVariantChange,
  fieldHighlight,
}: PagePreviewCardProps) {
  const label = mimeTypeLabel(page.mimeType);
  // DR-2004: sectionBounds live in the STORED (rotated) page's coordinate space, so
  // the hover band + section aspect-framing only apply to the rotated preview. When
  // showing the pre-rotation original, drop them — otherwise the band and box would
  // misalign to the differently-oriented un-rotated image.
  const sectionBounds = variant === 'original' ? null : (highlightBounds ?? null);
  const heading = sectionBounds?.heading?.trim();
  const highlightLabel =
    typeof sectionBounds?.order === 'number'
      ? `Section ${sectionBounds.order + 1}${heading ? `: “${heading}”` : ''}`
      : null;
  // When a section is highlighted, size the preview box to the parent page's real
  // aspect ratio (carried on the bounds) so the image fills it edge-to-edge and the
  // band lines up exactly — object-contain would otherwise letterbox a non-3/4 page
  // and the band would map to the padded box, not the image. Falls back to 3/4.
  const fieldAspect =
    fieldHighlight && fieldHighlight.pageWidth > 0 && fieldHighlight.pageHeight > 0
      ? `${fieldHighlight.pageWidth} / ${fieldHighlight.pageHeight}`
      : null;
  const boxAspect = (sectionBounds && sectionBandPct(sectionBounds)?.aspectRatio) || fieldAspect || '3 / 4';

  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden sticky top-[-100px]">
      <div className="px-4 py-5 sm:p-6 border-b border-gray-200 dark:border-zinc-700 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900 dark:text-zinc-100">{label} Preview</h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-zinc-400">
            View the document page as rendered {label.toLowerCase()}
          </p>
        </div>
        {/* DR-2004: only pages auto-rotated at split time have a pre-rotation
            original to fall back to. */}
        {page.preRotationHash && (
          <label className="flex shrink-0 items-center gap-2 text-sm text-gray-500 dark:text-zinc-400">
            Show original
            <Switch
              checked={variant === 'original'}
              onChange={(checked) => onVariantChange(checked ? 'original' : 'rotated')}
              aria-label="Show original"
            />
          </label>
        )}
      </div>
      <div className="p-6">
        {pdfUrl ? (
          <div className="bg-gray-200 p-3 dark:bg-zinc-700 shadow-[0_0_10px_rgba(0,0,0,0.1)] rounded-lg overflow-hidden">
            {/* Inner box hugs the image so the section band aligns to the page, not
                the padded frame. Sized to the page's aspect ratio while a section is
                highlighted (else 3/4), so object-contain fills it and the band is exact. */}
            <div className="relative group overflow-hidden" style={{ aspectRatio: boxAspect }}>
              <LazyImage src={pagePreviewSrc(page.id, { resize: 'x900', variant })} className="w-full h-full" />
              <SectionBandOverlay sectionBounds={sectionBounds} label={highlightLabel} />
              {fieldHighlight && (
                <FieldBoxOverlay
                  boxes={fieldHighlight.boxes}
                  pageWidth={fieldHighlight.pageWidth}
                  pageHeight={fieldHighlight.pageHeight}
                />
              )}
              <FullScreenImagePreviewTrigger pageId={page.id} variant={variant} />
            </div>
          </div>
        ) : (
          <div className="bg-gray-50 dark:bg-zinc-700 aspect-[3/4] flex items-center justify-center rounded-lg">
            <div className="text-center">
              <DocumentIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-400" />
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-400">Preview not available</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
